/**
 * Shared read-only detail lines for an insurance / corporate payer card.
 * Mirrors the binding the appointment-panel payment section
 * (`payment-section.tsx`) + Flutter use:
 *
 *   # {policyNumber}
 *   Insurance: {payerCompanyId} • TPA: {relatedTpa[0].code}   (insurance only)
 *   Eligibility Start Date: {start} • Expiration Date: {exp | <expired label>}
 *
 * Corporate omits the "Insurance: … TPA: …" codes line (pass no
 * `payerCompanyId` / `tpaCode`) — matching the Flutter corporate card.
 */

import type { ReactNode } from 'react';
// GAP (SYSTEM-RULES §3): no Signal DS glyph for "identifier / number" (`Hash`); it stays lucide in
// both looks, sized to the same 16px step as its DS neighbours when the switch is ON.
import { Hash, Info, CalendarDays } from 'lucide-react';

export function fmtPayerDate(iso?: string | null): string {
  if (!iso) return '—';
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return '—';
  return new Date(t).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

export function isPayerExpired(iso?: string | null): boolean {
  if (!iso) return false;
  const t = Date.parse(iso);
  return Number.isFinite(t) && t < Date.now();
}

/** Read `relatedTpa[0]` defensively (the type is `unknown[]` on the corporate
 *  envelope). Returns `{ code, companyName }` or null. */
export function firstTpa(
  relatedTpa: unknown,
): { code?: string; companyName?: string } | null {
  if (!Array.isArray(relatedTpa) || relatedTpa.length === 0) return null;
  const t = relatedTpa[0] as { code?: string; companyName?: string } | null;
  if (!t || typeof t !== 'object') return null;
  return { code: t.code, companyName: t.companyName };
}

/** Loose payer shape shared by insurance + corporate (the two source types
 *  differ structurally but carry the same display fields). */
export interface PayerLike {
  payerName?: string | null;
  name?: string | null;
  imageUrl?: string | null;
  payerCompanyId?: string | null;
  payerAgreementName?: string | null;
  relatedTpa?: unknown;
}

/** TPA-mediated title: `{TPA companyName} ({payerName})`; direct payers show
 *  just the payer name (CLAUDE.md dropdown-title rule). */
export function payerTitle(provider: PayerLike | null | undefined): string {
  const name = provider?.payerName ?? provider?.name ?? '';
  const tpa = firstTpa(provider?.relatedTpa);
  return tpa?.companyName ? `${tpa.companyName} (${name})` : name;
}

function payerInitials(name: string): string {
  return (
    name
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((w) => w[0])
      .join('')
      .toUpperCase() || '—'
  );
}

/**
 * Rich search-result row for a payer typeahead (insurance provider /
 * corporate sponsor). Mirrors the Flutter dropdown:
 *   [avatar]  {TPA companyName} ({payerName})
 *             {payerAgreementName}        ← green
 *             {TPA code} • {payerCompanyId}
 */
export function PayerSearchOption({ provider }: { provider: PayerLike }) {
  const name = provider.payerName ?? provider.name ?? '';
  const tpa = firstTpa(provider.relatedTpa);
  const title = tpa?.companyName ? `${tpa.companyName} (${name})` : name;
  const codes = [tpa?.code, provider.payerCompanyId].filter(Boolean).join(' • ');
  // Avatar initials come from the leading company in the title (the TPA
  // company when TPA-mediated, else the payer) — matches the reference "NC".
  const initials = payerInitials(tpa?.companyName ?? name);
  return (
    <div className="flex items-start gap-3">
      <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center overflow-hidden rounded-full bg-grey-100">
        {provider.imageUrl ? (
          <img src={provider.imageUrl} alt="" className="h-full w-full object-cover" />
        ) : (
          <span className="text-xs font-bold text-text-secondary">{initials}</span>
        )}
      </div>
      <div className="min-w-0">
        <p className="text-sm font-semibold leading-snug text-text-primary">{title}</p>
        {provider.payerAgreementName ? (
          <p className="text-xs font-semibold text-green-700">
            {provider.payerAgreementName}
          </p>
        ) : null}
        {codes ? <p className="text-xs text-text-muted">{codes}</p> : null}
      </div>
    </div>
  );
}

function Line({ icon, children }: { icon: ReactNode; children: ReactNode }) {
  return (
    <p className="flex items-center gap-2 text-sm text-text-secondary">
      <span className="text-text-muted">{icon}</span>
      {children}
    </p>
  );
}

export interface PayerReadLinesProps {
  policyNumber?: string | null;
  /** Insurance only — omit for corporate to hide the codes line. */
  payerCompanyId?: string | null;
  tpaCode?: string | null;
  eligibilityStart?: string | null;
  expirationDate?: string | null;
  /** Red label shown in place of the expiration date when expired. */
  expiredLabel: string;
}

export function PayerReadLines({
  policyNumber,
  payerCompanyId,
  tpaCode,
  eligibilityStart,
  expirationDate,
  expiredLabel,
}: PayerReadLinesProps) {
  const expired = isPayerExpired(expirationDate);
  const showCodes = !!payerCompanyId || !!tpaCode;
  return (
    <div className="mt-1 space-y-1">
      <Line icon={<Hash className="size-4" />}>
        Member ID:{' '}
        <span className="text-text-primary">{policyNumber || '—'}</span>
      </Line>
      {showCodes ? (
        <Line icon={<Info className="size-4" />}>
          Insurance:{' '}
          <span className="text-text-primary">{payerCompanyId || '—'}</span>
          {tpaCode ? (
            <>
              {' • '}TPA: <span className="text-text-primary">{tpaCode}</span>
            </>
          ) : null}
        </Line>
      ) : null}
      <Line icon={<CalendarDays className="size-4" />}>
        Eligibility Start Date:{' '}
        <span className="text-text-primary">{fmtPayerDate(eligibilityStart)}</span>
        {' • '}Expiration Date:{' '}
        {expired ? (
          <span className="font-semibold text-status-error">{expiredLabel}</span>
        ) : (
          <span className="text-text-primary">{fmtPayerDate(expirationDate)}</span>
        )}
      </Line>
    </div>
  );
}
